import { Badge, Button, Group, Select, Text } from "@mantine/core";
import { IconPlus, IconTags } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { DataTable, FilterBar, PageHeader, useSearchParamState } from "@how/ui";
import { CategoriesModal } from "./CategoriesModal";
import { GroupDrawer } from "./GroupDrawer";
import { GroupModal } from "./GroupModal";
import {
  DAY_LABEL,
  JOINING_LABEL,
  VISIBILITY_LABEL,
  formatMeetingTime,
  useCampusOptions,
  useGroupCategories,
  useGroups,
  useMyLedGroupIds,
  type GroupRow,
} from "./group-queries";
import { semanticColor, type SemanticColor } from "@how/design-system";

const VISIBILITY_COLOR: Record<GroupRow["visibility"], SemanticColor> = {
  public: semanticColor("success"),
  unlisted: semanticColor("neutral"),
  private: semanticColor("special"),
};

const columns: ColumnDef<GroupRow>[] = [
  {
    accessorKey: "name",
    header: "Group",
    enableSorting: false,
    cell: (ctx) => (
      <div>
        <Text size="sm" fw={600}>
          {ctx.row.original.name}
        </Text>
        {ctx.row.original.categoryName === null ? null : (
          <Text size="xs" c="dimmed">
            {ctx.row.original.categoryName}
          </Text>
        )}
      </div>
    ),
  },
  {
    accessorKey: "meetingDay",
    header: "Meets",
    enableSorting: false,
    cell: (ctx) => {
      const g = ctx.row.original;
      if (g.meetingDay === null && g.meetingLocation === null)
        return (
          <Text size="sm" c="dimmed">
            Not set
          </Text>
        );
      const when = [
        g.meetingDay === null ? null : DAY_LABEL[g.meetingDay],
        formatMeetingTime(g.meetingTime),
      ]
        .filter((part): part is string => part !== null)
        .join(" ");
      return (
        <div>
          <Text size="sm">{when.length === 0 ? "—" : when}</Text>
          {g.meetingLocation === null ? null : (
            <Text size="xs" c="dimmed">
              {g.meetingLocation}
            </Text>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "campusName",
    header: "Campus",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.campusName === null ? (
        <Text size="sm" c="dimmed">
          None
        </Text>
      ) : (
        <Text size="sm">{ctx.row.original.campusName}</Text>
      ),
  },
  {
    accessorKey: "leaders",
    header: "Leaders",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.leaders.length === 0 ? (
        <Text size="sm" c="dimmed">
          —
        </Text>
      ) : (
        <Text size="sm">{ctx.row.original.leaders.join(", ")}</Text>
      ),
  },
  {
    accessorKey: "activeMembers",
    header: "Members",
    enableSorting: false,
    meta: { numeric: true },
    cell: (ctx) =>
      ctx.row.original.capacity === null
        ? ctx.row.original.activeMembers
        : `${String(ctx.row.original.activeMembers)} / ${String(ctx.row.original.capacity)}`,
  },
  {
    accessorKey: "pendingRequests",
    header: "Waiting",
    enableSorting: false,
    meta: { numeric: true },
    cell: (ctx) =>
      ctx.row.original.pendingRequests === 0 ? (
        <Text size="sm" c="dimmed">
          0
        </Text>
      ) : (
        <Badge variant="light" color="yellow">
          {ctx.row.original.pendingRequests}
        </Badge>
      ),
  },
  {
    accessorKey: "visibility",
    header: "Visibility",
    enableSorting: false,
    cell: (ctx) => (
      <Group gap={6} wrap="nowrap">
        <Badge variant="light" color={VISIBILITY_COLOR[ctx.row.original.visibility]}>
          {VISIBILITY_LABEL[ctx.row.original.visibility]}
        </Badge>
        <Text size="xs" c="dimmed">
          {JOINING_LABEL[ctx.row.original.joiningRule]}
        </Text>
      </Group>
    ),
  },
  {
    accessorKey: "isActive",
    header: "Status",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.isActive ? (
        <Badge variant="light" color="teal">
          Active
        </Badge>
      ) : (
        <Badge variant="light" color="gray">
          Archived
        </Badge>
      ),
  },
];

const PAGE = { pageIndex: 0, pageSize: 200 };
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "Active and archived" },
];

/**
 * The church's groups (ADR 0040). Staff with groups.read see every group; a leader with only groups.read_assigned
 * sees the ones they lead, because the screen asks the database which those are and lists no others. A row opens
 * the record: seasons, roster, requests. Adding and archiving take groups.manage; editing a group you lead does not.
 */
export function GroupsPage() {
  const canReadAll = usePermission("groups.read");
  const canManage = usePermission("groups.manage", { write: true });
  const led = useMyLedGroupIds();
  const categories = useGroupCategories();
  const campuses = useCampusOptions();
  const [search, setSearch] = useSearchParamState("q", "");
  const [categoryId, setCategoryId] = useSearchParamState("category", "");
  const [campusId, setCampusId] = useSearchParamState("campus", "");
  const [status, setStatus] = useSearchParamState("status", "active");
  const [selected, setSelected] = useSearchParamState("group", "");
  const [adding, setAdding] = useState(false);
  const [managingCategories, setManagingCategories] = useState(false);

  const scoped = canReadAll.data === false;
  const groups = useGroups({
    search,
    ...(categoryId === "" ? {} : { categoryId }),
    ...(campusId === "" ? {} : { campusId }),
    status: status === "archived" || status === "all" ? status : "active",
    ...(scoped ? { onlyIds: led.data ?? [] } : {}),
  });
  const tableStatus =
    groups.isPending || canReadAll.isPending || (scoped && led.isPending)
      ? "loading"
      : groups.isError
        ? "error"
        : "ready";

  return (
    <>
      <PageHeader
        title="Groups"
        description={
          scoped
            ? "The groups you lead. Their rosters and requests are yours to manage."
            : "Every group, who leads it, who is in it this season, and who is waiting to join."
        }
        actions={
          canManage.data === true ? (
            <Group gap="sm">
              <Button
                variant="light"
                leftSection={<IconTags size={16} />}
                onClick={() => {
                  setManagingCategories(true);
                }}
              >
                Categories
              </Button>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setAdding(true);
                }}
              >
                Add group
              </Button>
            </Group>
          ) : undefined
        }
      />
      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name"
        filters={
          <>
            <Select
              aria-label="Filter by category"
              placeholder="Any category"
              clearable
              data={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              value={categoryId === "" ? null : categoryId}
              onChange={(value) => {
                setCategoryId(value ?? undefined);
              }}
              w={200}
            />
            <Select
              aria-label="Filter by campus"
              placeholder="Any campus"
              clearable
              data={campuses.data ?? []}
              value={campusId === "" ? null : campusId}
              onChange={(value) => {
                setCampusId(value ?? undefined);
              }}
              w={200}
            />
            <Select
              aria-label="Filter by status"
              data={STATUS_OPTIONS}
              value={status}
              allowDeselect={false}
              onChange={(value) => {
                setStatus(value === null || value === "active" ? undefined : value);
              }}
              w={190}
            />
          </>
        }
      />
      <DataTable
        label="Groups"
        columns={columns}
        rows={groups.data ?? []}
        totalRows={groups.data?.length ?? 0}
        status={tableStatus}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setSelected(row.id);
        }}
        emptyTitle={scoped ? "You lead no groups yet" : "No groups"}
        emptyDescription={
          scoped
            ? "When the church makes you a leader of a group, it appears here."
            : status === "active"
              ? "Add the first group, or show archived ones."
              : "Nothing matches."
        }
        errorMessage="Could not load groups."
        onRetry={() => {
          void groups.refetch();
        }}
      />
      <GroupDrawer
        groupId={selected === "" ? null : selected}
        onClose={() => {
          setSelected(undefined);
        }}
      />
      <GroupModal
        key={adding ? "new" : "closed"}
        group={adding ? null : undefined}
        canManage={canManage.data === true}
        onClose={() => {
          setAdding(false);
        }}
        onCreated={(id) => {
          setAdding(false);
          setSelected(id);
        }}
      />
      <CategoriesModal
        opened={managingCategories}
        onClose={() => {
          setManagingCategories(false);
        }}
      />
    </>
  );
}
